import {
    TextField,
    DateField,
    useDataProvider,
    SimpleShowLayout,
    NumberField,
    Labeled,
    TopToolbar,
    useNotify,
    RecordContextProvider,
} from "react-admin";
import { Box, Typography, Paper, Chip, Alert } from "@mui/material";
import { useEffect, useState } from "react";
import BackButton from "../../Components/BackButton";
import { useParams } from "react-router";
import dayjs from "dayjs";

// 状态选项定义（与列表页保持一致）
const STATUS_CHOICES = [
    { id: 1, name: "审核中" },
    { id: 2, name: "打款中" },
    { id: 3, name: "提现异常" },
    { id: 4, name: "已结清" }
];

// 状态颜色映射
const STATUS_COLORS = {
    1: "default",    // 审核中
    2: "primary",    // 打款中
    3: "error",      // 提现异常
    4: "success"     // 已结清
};

// 状态显示组件
const StatusField = ({ record }: { record?: any }) => {
    if (!record) return null;

    const status = STATUS_CHOICES.find(choice => choice.id === record.status);
    const color = STATUS_COLORS[record.status as keyof typeof STATUS_COLORS] || "default";

    return (
        <Chip
            label={status ? status.name : record.status}
            color={color}
            variant="outlined"
        />
    );
};

const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
};

// 详情页面组件
export const outboundProductDetail = () => {
    const [hasError, setHasError] = useState(false);
    const [record, setRecord] = useState({})
    const dataProvider = useDataProvider();
    const params = useParams(); // 获取URL参数
    const outStockBatchNo = params.id; // 提取设备ID
    const notify = useNotify();

    // 构建返回设备详情页的路径
    const backPath = `/outboundProduct/`;

    useEffect(() => {
        const fetchData = async () => {
            if (!outStockBatchNo) {
                setHasError(true);
                return;
            }

            try {
                const response = await dataProvider.getList('outboundProduct', {
                    filter: {
                        outStockBatchNo: outStockBatchNo,
                        status: 1
                    },
                    pagination: { page: 1, perPage: 10 },
                    sort: { field: 'id', order: 'DESC' }
                });

                if (response.data && response.data.length > 0) {
                    setRecord(response.data[0]);
                } else {
                    setRecord({});
                }
                setHasError(false);
            } catch (error) {
                console.error("获取出库详情失败:", error);
                setHasError(true);
                notify('获取出库详情失败', { type: 'error' });
            }
        };

        fetchData();
    }, [outStockBatchNo, dataProvider, notify]);


    let getStast = () => {
        if (hasError) {
            return (
                <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
                    <Typography variant="h6" color="error">
                        无法加载出库详情
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        请检查网络连接或联系管理员
                    </Typography>
                </Box>

            );
        }

        if (!record) {
            return (
                <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
                    <Alert severity="warning">
                        未找到出库记录
                    </Alert>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex' }}>
                {/* 基础信息卡片 */}
                <Paper sx={{ p: 3, mb: 3, mr: 3, minWidth: 300 }}>
                    <Typography variant="h6" gutterBottom sx={{
                        borderBottom: '1px solid #e0e0e0',
                        pb: 1,
                        mb: 2
                    }}>
                        出库基本信息
                    </Typography>

                    <RecordContextProvider value={record}>
                        <SimpleShowLayout>
                            <TextField source="id" label="ID" />
                            <TextField source="outStockBatchNo" label="出库批次号" />
                            <TextField source="productModel" label="产品型号" />
                            <NumberField source="outStockCount" label="出库数量" />

                            <Labeled label="状态">
                                <StatusField record={record} />
                            </Labeled>

                            <TextField source="logisticsNo" label="物流单号" />
                        </SimpleShowLayout>
                    </RecordContextProvider>
                </Paper>

                {/* 关联信息卡片 */}
                <Paper sx={{ p: 3, mb: 3, mr: 3, minWidth: 300 }}>
                    <Typography variant="h6" gutterBottom sx={{
                        borderBottom: '1px solid #e0e0e0',
                        pb: 1,
                        mb: 2
                    }}>
                        关联信息
                    </Typography>

                    <RecordContextProvider value={record}>
                        <SimpleShowLayout>
                            <NumberField source="deviceType" label="设备类型" />
                            <NumberField source="manufacturerId" label="制造商ID" />
                            <NumberField source="saleCustomerId" label="销售客户ID" />
                        </SimpleShowLayout>
                    </RecordContextProvider>
                </Paper>

                {/* 地址和时间信息卡片 */}
                <Paper sx={{ p: 3, mb: 3, minWidth: 300 }}>
                    <Typography variant="h6" gutterBottom sx={{
                        borderBottom: '1px solid #e0e0e0',
                        pb: 1,
                        mb: 2
                    }}>
                        地址和时间信息
                    </Typography>


                    <RecordContextProvider value={record}>
                        <SimpleShowLayout>
                            <TextField source="shippingAddress" label="收货地址" />

                            <Labeled label="创建时间">
                                <Typography>
                                    {formatTimestamp(record.createTime)}
                                </Typography>
                            </Labeled>

                            <Labeled label="出库日期">
                                <Typography>
                                    {record.outStockDate ? formatTimestamp(record.outStockDate) : "-"}
                                </Typography>
                            </Labeled>

                            {record.updateTime && (
                                <Labeled label="更新时间">
                                    <Typography>
                                        {formatTimestamp(record.updateTime)}
                                    </Typography>
                                </Labeled>
                            )}
                        </SimpleShowLayout>
                    </RecordContextProvider>
                </Paper>
            </Box>
        );

    }

    return (
        <>
            <TopToolbar sx={{ justifyContent: 'start' }}>
                <BackButton to={backPath} />
            </TopToolbar>
            {getStast()}
        </>

    );
};

// 为了保持与App.tsx中的导入一致，导出默认组件
export default outboundProductDetail;